import { useEffect, useRef, useState } from 'react';

// A finished recording becomes text in the field: the Studio transcribes it (private
// upload, one quoted credit) and the transcript joins what was already typed, for the
// creator to review before sending. A new recording replaces the attached one.
export default function useVoiceDraft(s, capture, value) {
  const [status, setStatus] = useState('idle');
  const handled = useRef(null);
  const typed = useRef(value);
  const alive = useRef(true);
  typed.current = value;
  useEffect(() => () => { alive.current = false; }, []);

  const transcribe = async blob => {
    setStatus('transcribing');
    const transcription = await s.transcribe(blob, capture.mimeType);
    if (!alive.current) return;
    const text = transcription?.status === 'ready' ? (transcription.text || '').trim() : '';
    if (!text) {
      if (transcription) s.setError('transcription');
      setStatus('failed');
      return;
    }
    const draft = typed.current.trim();
    s.attachVoice(transcription, draft ? `${draft}\n\n${text}` : text);
    setStatus('ready');
  };
  useEffect(() => {
    if (capture.status !== 'ready' || !capture.blob || handled.current === capture.blob) return;
    handled.current = capture.blob;
    transcribe(capture.blob);
  }, [capture.status, capture.blob]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => { capture.clear(); setStatus('idle'); };
  // Once the attached transcription is gone (sent with the prompt or the message,
  // or dropped by the Studio), so is its recording.
  const attached = useRef(null);
  useEffect(() => {
    if (s.voice) { attached.current = s.voice.id; return; }
    if (!attached.current) return;
    attached.current = null;
    reset();
  }, [s.voice]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    status,
    retry: () => capture.blob && transcribe(capture.blob),
    // Only the recording goes: its transcript stays in the field as typed text.
    discard: () => { s.discardVoice(); reset(); },
  };
}
